// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StepperContext } from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { CarrierDto } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        cartPage: getPageLinkByPageType(state, "CartPage"),
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
        cart: cartState.value,
    };
};

const mapDispatchToProps = { setPlaceOrderErrorMessage };

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutStepperStyles {
    wrapperStyle: InjectableCss;
    stepperItemWrapper: TypographyProps;
}

export const styles: CheckoutStepperStyles = {
    wrapperStyle: {
        css: css`
            display: flex;
            column-gap: 8px;
            padding: 0;
            list-style: none;
            position: relative;
            padding-bottom: 12px;
            border-bottom: 1px solid ${color("common.ui3")};
            margin-bottom: 16px;
        `,
    },
    stepperItemWrapper: {
        ...fontConfig.singleLineRegular,
        weight: fontConfig.weight.medium,
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            text-transform: uppercase;
            column-gap: 8px;
            opacity: 0.5;
            &:after {
                content: " ";
                display: inline;
                width: 20px;
                height: 1px;
                background: ${color("common.ui4")};
            }

            &:last-child:after {
                display: none;
            }
            ${xScreen("lte", "md")} {
                div > span {
                    display: none;
                }
            }

            &.active {
                opacity: 1;
                color: ${color("primary.main")};
            }

            &.completed {
                opacity: 1;
                color: ${color("common.ui6")};
            }

            &.clickable {
                cursor: pointer;
                opacity: 1;
                &:hover {
                    color: ${color("common.ui8")};
                }
            }
        `,
    },
};

const CheckoutStepper: React.FC<Props> = ({
    history,
    isPickUp,
    cartPage,
    checkoutShippingPage,
    cart,
    setPlaceOrderErrorMessage,
}) => {
    const { currentStep, maxStep, setCurrentStep } = useContext(StepperContext);
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);

    const steps: any = ["Cart", "Addresses", isPickUp ? "PickUp" : "Shipping", "Payment", "Review"];

    const goTo = (i: number) => {
        if (i === 0) {
            history.push(cartPage?.url as string);
        } else if (i === 1) {
            history.push(checkoutShippingPage?.url as string);
        } else if (i === 3 && !selectedCarrier) {
            return;
        }
        setCurrentStep(i);
        setPlaceOrderErrorMessage({});
    };

    useEffect(() => {
        if (cart) {
            const selectedCarrier = cart?.carriers?.find(c => c.id === cart?.carrier?.id) || cart?.carrier;
            setSelectedCarrier(selectedCarrier);
        }
    }, [cart?.carrier, cart?.shipVia]);

    return (
        <StyledWrapper>
            <Typography as="ul" {...styles.wrapperStyle}>
                {steps &&
                    steps.length > 0 &&
                    steps.map((item: any, index: number) => {
                        if (index === 0) {
                            return null;
                        }
                        return (
                            <Typography
                                as="li"
                                key={`stepper${item + index}`}
                                {...styles.stepperItemWrapper}
                                className={`${currentStep === index && "active"} ${
                                    currentStep > index && "completed"
                                } ${index <= maxStep && "clickable"}`}
                                onClick={() => index <= maxStep && goTo(index)}
                            >
                                <Typography as="div">
                                    {index}.<span>{item}</span>
                                </Typography>
                            </Typography>
                        );
                    })}
            </Typography>
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Checkout - Shipping",
    allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
    displayName: "Checkout Stepper",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutStepper)),
    definition,
};

export default widgetModule;
